import styled, { keyframes } from 'styled-components';

import { ActionStatus, WorkflowAction } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import CircleCheckIcon from 'svg/CircleCheckIcon';

import { completeWithParked } from './referralProgress';

export type StatusIndicator = 'complete' | 'running' | 'review' | 'attention' | 'error' | 'pending' | 'muted';

// One action's visual state. `review` (action_required) is ready for a person to act;
// `attention` (flagged / stale) means the step is blocked or its inputs changed.
export function statusIndicator(action: WorkflowAction): StatusIndicator {
  if (action.status === ActionStatus.CANCELED || action.status === ActionStatus.SKIPPED) return 'muted';
  if (action.stale) return 'attention';
  switch (action.status) {
    case ActionStatus.COMPLETED:
      return 'complete';
    case ActionStatus.RUNNING:
      return 'running';
    case ActionStatus.ACTION_REQUIRED:
      return 'review';
    case ActionStatus.FLAGGED:
      return 'attention';
    case ActionStatus.ERRORED:
      return 'error';
    default:
      return 'pending';
  }
}

// A category's roll-up (design §12): blocked/changed wins, then ready-for-review, then
// error, then done-when-all-done, then still-running, else waiting.
export function rollupIndicator(actions: WorkflowAction[]): StatusIndicator {
  if (actions.some((a) => a.isFlagged || a.stale)) return 'attention';
  if (actions.some((a) => a.isActionRequired)) return 'review';
  if (actions.some((a) => a.status === ActionStatus.ERRORED)) return 'error';
  if (completeWithParked(actions)) return 'complete';
  if (actions.some((a) => a.status === ActionStatus.RUNNING)) return 'running';
  if (
    actions.length > 0 &&
    actions.every((a) => a.status === ActionStatus.CANCELED || a.status === ActionStatus.SKIPPED)
  )
    return 'muted';
  return 'pending';
}

// The accent color a title/text takes for a given state (matches the icon).
export function indicatorColor(indicator: StatusIndicator): string | undefined {
  if (indicator === 'running') return colors.aiPurple;
  if (indicator === 'review') return colors.primaryBlue;
  if (indicator === 'attention') return '#c0552f'; // the warning-tone text color (see StatusBadge)
  if (indicator === 'error') return colors.accentRed;
  return undefined;
}

export default function StatusIcon({ indicator, size = 18 }: { indicator: StatusIndicator; size?: number }) {
  if (indicator === 'complete') return <CircleCheckIcon width={size} height={size} fill={colors.actionGreen} />;
  if (indicator === 'running') return <Spinner $size={size} role='img' aria-label='Running' />;
  if (indicator === 'review')
    return (
      <ReviewRing $size={size} role='img' aria-label='Ready for review'>
        <svg width={size * 0.58} height={size * 0.58} viewBox='0 0 12 9' fill='none'>
          <path
            d='M1 4.6 L4.2 7.6 L11 1'
            stroke='currentColor'
            strokeWidth='1.8'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </ReviewRing>
    );
  if (indicator === 'attention')
    return (
      <Glyph $size={size} $bg={colors.secondaryOrange}>
        !
      </Glyph>
    );
  if (indicator === 'error')
    return (
      <Glyph $size={size} $bg={colors.accentRed}>
        !
      </Glyph>
    );
  return <Hollow $size={size} $muted={indicator === 'muted'} />;
}

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span<{ $size: number }>`
  display: inline-block;
  box-sizing: border-box;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 2px solid ${colors.aiPurple25};
  border-top-color: ${colors.aiPurple};
  animation: ${spin} 0.8s linear infinite;
`;

const ReviewRing = styled.span<{ $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 1.5px solid ${colors.primaryBlue};
  color: ${colors.primaryBlue};
`;

const Glyph = styled.span<{ $size: number; $bg: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  color: #fff;
  font-size: ${({ $size }) => Math.round($size * 0.66)}px;
  font-weight: 700;
  line-height: 1;
`;

const Hollow = styled.span<{ $size: number; $muted: boolean }>`
  display: inline-block;
  box-sizing: border-box;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 1.5px solid ${colors.black25};
  opacity: ${({ $muted }) => ($muted ? 0.5 : 1)};
`;
